import { useMemo } from 'react';
import type { PatientDetailsCardProps } from './types/patientDetailsCard.types';
import { readPreferredStaffIds } from './types/patientDetailsCard.types';
import { resolvePreferredStaff } from './preferredStaffHelpers';
import { usePatientForm } from '../../hooks/usePatientForm';
import { useStaffOptions } from '../../hooks/usePreferredStaffOptions';
import { PatientDetailsView } from './PatientDetailsView';
import { PatientDetailsForm } from './PatientDetailsForm';
import styles from './PatientDetailsCard.module.css';

export type { PatientDetailsCardProps };

// TODO: replace custom elements with components from qampo-ui-library
export const PatientDetailsCard = ({
  patient,
  locationId,
  mode,
  createPrefill,
  onEditStart,
  onEditEnd,
  onPatientUpdated,
  onPatientCreated
}: PatientDetailsCardProps) => {
  const { staff, competenceNameById, isLoaded, hasError, retry } = useStaffOptions(locationId);

  const {
    form,
    setForm,
    fieldErrors,
    patientRoomNeeds,
    allRoomProperties,
    handleEditClick,
    handleCreatePatient,
    handleUpdatePatient
  } = usePatientForm({
    patient,
    locationId,
    mode,
    createPrefill,
    onEditStart,
    onEditEnd,
    onPatientUpdated,
    onPatientCreated
  });

  // Reads the patient's own stored ids rather than the form, since this is the server-state view.
  // Display-only: a miss costs a row here, never the stored id.
  const storedPreferredStaffIds = useMemo(
    () => (patient ? readPreferredStaffIds(patient) : []),
    [patient]
  );
  const patientPreferredStaff = useMemo(
    () => resolvePreferredStaff(storedPreferredStaffIds, staff),
    [storedPreferredStaffIds, staff]
  );

  const isEditing = mode === 'edit' || mode === 'create';

  return (
    <div className={styles.card}>
      {!isEditing && patient ? (
        <PatientDetailsView
          patient={patient}
          patientRoomNeeds={patientRoomNeeds}
          patientPreferredStaff={patientPreferredStaff}
          hasStoredPreferredStaff={storedPreferredStaffIds.length > 0}
          competenceNameById={competenceNameById}
          isStaffLoaded={isLoaded}
          hasStaffError={hasError}
          onRetryStaff={retry}
          onEditClick={handleEditClick}
        />
      ) : (
        <PatientDetailsForm
          form={form}
          setForm={setForm}
          fieldErrors={fieldErrors}
          mode={mode === 'create' ? 'create' : 'edit'}
          allRoomProperties={allRoomProperties}
          allStaff={staff}
          competenceNameById={competenceNameById}
          isStaffLoaded={isLoaded}
          hasStaffError={hasError}
          onRetryStaff={retry}
          onSave={mode === 'create' ? handleCreatePatient : handleUpdatePatient}
        />
      )}
    </div>
  );
};
